import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CartItem } from 'src/app/model/cart';
import { PRODUCT_PLACEHOLDER } from '../../store.constants';

const SCROLL_LOCK_CLASS = 'is-scroll-locked';

/** Painel lateral do carrinho: itens, resumo de pontos, telefone e confirmacao. */
@Component({
  selector: 'app-store-cart-drawer',
  templateUrl: './store-cart-drawer.component.html',
  styleUrls: ['./store-cart-drawer.component.scss'],
})
export class StoreCartDrawerComponent implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() items: CartItem[] = [];
  @Input() balance = 0;
  @Input() submitting = false;
  @Input() phone = '';
  /** Saldo nao pode ser lido: a troca nao tem como ser validada. */
  @Input() balanceUnknown = false;

  @Output() closed = new EventEmitter<void>();
  @Output() increment = new EventEmitter<string>();
  @Output() decrement = new EventEmitter<string>();
  @Output() removed = new EventEmitter<string>();
  @Output() phoneChange = new EventEmitter<string>();
  @Output() confirmed = new EventEmitter<void>();

  @ViewChild('closeButton', { static: false }) closeButton: ElementRef<HTMLButtonElement>;
  @ViewChild('panel', { static: false }) panel: ElementRef<HTMLElement>;

  /** Mostra o erro do telefone so depois da primeira tentativa. */
  phoneTouched = false;

  readonly placeholder = PRODUCT_PLACEHOLDER;

  constructor(private renderer: Renderer2) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.open) {
      return;
    }
    if (this.open) {
      this.lockScroll(true);
      this.phoneTouched = false;
      // Espera a renderizacao do painel antes de mover o foco.
      setTimeout(() => {
        if (this.closeButton) {
          this.closeButton.nativeElement.focus();
        }
      }, 0);
    } else {
      this.lockScroll(false);
    }
  }

  ngOnDestroy(): void {
    // Sair da rota com o painel aberto deixaria a app inteira sem scroll.
    this.lockScroll(false);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) {
      this.closed.emit();
    }
  }

  get total(): number {
    return (this.items || []).reduce((sum, item) => sum + item.quantity * item.unitValue, 0);
  }

  get totalItems(): number {
    return (this.items || []).reduce((sum, item) => sum + item.quantity, 0);
  }

  get balanceAfter(): number {
    return (this.balance || 0) - this.total;
  }

  get isEmpty(): boolean {
    return !this.items || this.items.length === 0;
  }

  /** ngx-mask entrega so digitos; o telefone brasileiro tem 10 ou 11. */
  get phoneValid(): boolean {
    return /^\d{10,11}$/.test((this.phone || '').replace(/\D/g, ''));
  }

  get canConfirm(): boolean {
    return (
      !this.isEmpty && !this.submitting && this.phoneValid && !this.blockReason
    );
  }

  /** Por que a troca esta travada, fora o telefone. Vazio quando da para confirmar. */
  get blockReason(): string {
    if (this.balanceUnknown) {
      return 'clicStore.balanceUnknownBlock';
    }
    return this.balanceAfter < 0 ? 'clicStore.notEnoughBalanceBlock' : '';
  }

  onPhoneInput(value: string): void {
    this.phoneChange.emit(value);
  }

  onConfirm(): void {
    this.phoneTouched = true;
    if (!this.canConfirm) {
      return;
    }
    this.confirmed.emit();
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img && !img.src.endsWith(PRODUCT_PLACEHOLDER)) {
      img.src = PRODUCT_PLACEHOLDER;
    }
  }

  subtotal(item: CartItem): number {
    return item.quantity * item.unitValue;
  }

  trackByAwardId(_: number, item: CartItem): string {
    return item.awardId;
  }

  /**
   * Prende o Tab dentro do painel. Sem isso o foco sai para o conteudo atras,
   * que continua visivel.
   */
  @HostListener('document:keydown.tab', ['$event'])
  @HostListener('document:keydown.shift.tab', ['$event'])
  onTab(event: KeyboardEvent): void {
    if (!this.open || !this.panel) {
      return;
    }
    const focusables = this.panel.nativeElement.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) {
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement as HTMLElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private lockScroll(lock: boolean): void {
    if (lock) {
      this.renderer.addClass(document.body, SCROLL_LOCK_CLASS);
    } else {
      this.renderer.removeClass(document.body, SCROLL_LOCK_CLASS);
    }
  }
}
